import React, { useState } from "react";
import { CalendarDays, Coffee, Sun, Moon, Plus } from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { MicroInteraction } from "../ui/micro-interactions";

interface MealCoursePickerProps {
  onAddToPlan: (dayIndex: number, mealType: string) => void;
  className?: string;
  disabled?: boolean;
}

interface MealOption {
  type: string;
  label: string;
  icon: React.ReactNode;
  description: string;
}

interface DayOption {
  index: number;
  label: string;
  shortLabel: string;
}

const MEAL_OPTIONS: MealOption[] = [
  {
    type: "breakfast",
    label: "Breakfast",
    icon: <Coffee className="h-4 w-4" />,
    description: "Start your day",
  },
  {
    type: "lunch",
    label: "Lunch",
    icon: <Sun className="h-4 w-4" />,
    description: "Midday meal",
  },
  {
    type: "dinner",
    label: "Dinner",
    icon: <Moon className="h-4 w-4" />,
    description: "Evening meal",
  },
];

// Get the current week's days starting from today
const getCurrentWeekDays = (): DayOption[] => {
  const today = new Date();
  const currentDayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const days: DayOption[] = [];

  for (let i = 0; i < 7; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() - currentDayOfWeek + i);

    const isToday = i === currentDayOfWeek;
    const isTomorrow = i === currentDayOfWeek + 1;

    let label = date.toLocaleDateString(undefined, {
      weekday: "long",
      month: "short",
      day: "numeric",
    });
    if (isToday)
      label = `Today (${date.toLocaleDateString(undefined, { weekday: "short" })})`;
    else if (isTomorrow)
      label = `Tomorrow (${date.toLocaleDateString(undefined, { weekday: "short" })})`;

    days.push({
      index: i,
      label,
      shortLabel: date.toLocaleDateString(undefined, { weekday: "short" }),
    });
  }

  return days;
};

export function MealCoursePicker({
  onAddToPlan,
  className,
  disabled,
}: MealCoursePickerProps) {
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [selectedMeal, setSelectedMeal] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  const weekDays = getCurrentWeekDays();

  const handleAddToPlan = async () => {
    if (selectedDay !== null && selectedMeal) {
      setIsAdding(true);
      try {
        await onAddToPlan(selectedDay, selectedMeal);
        setOpen(false);
        // Reset selections for next time
        setSelectedDay(null);
        setSelectedMeal(null);
      } finally {
        setIsAdding(false);
      }
    }
  };

  const selectedDayLabel =
    selectedDay !== null ? weekDays[selectedDay]?.shortLabel : "";
  const selectedMealLabel = selectedMeal
    ? MEAL_OPTIONS.find((m) => m.type === selectedMeal)?.label
    : "";

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <MicroInteraction
          animation="scale"
          interaction="tap"
          scale={0.95}
          duration={100}
        >
          <Button
            variant="secondary"
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
            }}
            className={`min-h-[44px] text-sm font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2 active:bg-coral/10 ${className}`}
            aria-label="Add recipe to meal plan"
            aria-haspopup="dialog"
          >
            <Plus className="mr-1 h-4 w-4" />
            Add to Plan
          </Button>
        </MicroInteraction>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="flex items-center gap-2">
          <CalendarDays className="h-4 w-4" />
          Choose Day & Meal
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Day Selection */}
        <div className="px-2 py-1">
          <p className="mb-2 text-xs font-medium text-gray-500">Select Day:</p>
          <div className="grid grid-cols-2 gap-1">
            {weekDays.map((day) => (
              <MicroInteraction
                key={day.index}
                animation="scale"
                interaction="tap"
                scale={0.95}
                duration={100}
              >
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedDay(day.index);
                  }}
                  className={`min-h-[44px] w-full rounded border p-2 text-left text-xs transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2 ${
                    selectedDay === day.index
                      ? "border-sage bg-sage/20 font-medium text-sage"
                      : "border-gray-200 desktop-hover:border-sage/30 desktop-hover:bg-sage/5"
                  }`}
                  aria-label={`Select ${day.label} for meal plan`}
                  aria-pressed={selectedDay === day.index}
                  role="button"
                >
                  {day.label}
                </button>
              </MicroInteraction>
            ))}
          </div>
        </div>

        <DropdownMenuSeparator />

        {/* Meal Selection */}
        <div className="px-2 py-1">
          <p className="mb-2 text-xs font-medium text-gray-500">Select Meal:</p>
          {MEAL_OPTIONS.map((meal) => (
            <MicroInteraction
              key={meal.type}
              animation="translate"
              interaction="tap"
              translation={{ x: 4 }}
              duration={100}
            >
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedMeal(meal.type);
                }}
                className={`flex min-h-[44px] w-full items-center gap-3 rounded p-2 text-left transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2 ${
                  selectedMeal === meal.type
                    ? "border border-sage bg-sage/20 text-sage"
                    : "desktop-hover:bg-sage/5"
                }`}
                aria-label={`Select ${meal.label} for meal plan`}
                aria-pressed={selectedMeal === meal.type}
                role="button"
              >
                <span
                  className={
                    selectedMeal === meal.type ? "text-sage" : "text-gray-400"
                  }
                >
                  {meal.icon}
                </span>
                <div className="flex-1">
                  <div className="text-sm font-medium">{meal.label}</div>
                  <div className="text-xs text-gray-500">
                    {meal.description}
                  </div>
                </div>
              </button>
            </MicroInteraction>
          ))}
        </div>

        <DropdownMenuSeparator />

        {/* Add Button */}
        <div className="p-2">
          <MicroInteraction
            animation="scale"
            interaction="tap"
            scale={0.95}
            duration={100}
          >
            <Button
              variant="secondary"
              onClick={handleAddToPlan}
              disabled={selectedDay === null || !selectedMeal || isAdding}
              className="min-h-[44px] w-full text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2 active:bg-coral/10"
              aria-label={
                isAdding
                  ? "Adding recipe to meal plan..."
                  : selectedDay !== null && selectedMeal
                    ? `Add recipe to ${selectedDayLabel} ${selectedMealLabel}`
                    : "Choose day and meal to add recipe to plan"
              }
            >
              {isAdding ? (
                <>
                  <div className="mr-2 h-3 w-3 animate-spin rounded-full border border-white border-t-transparent" />
                  Adding...
                </>
              ) : selectedDay !== null && selectedMeal ? (
                `Add to ${selectedDayLabel} ${selectedMealLabel}`
              ) : (
                "Choose day & meal"
              )}
            </Button>
          </MicroInteraction>
          {selectedDay !== null && selectedMeal && (
            <p className="mt-1 text-center text-xs text-gray-500">
              Recipe will be added to your meal plan
            </p>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
